import { useCallback, useEffect, useMemo, useRef } from "react";
import { areFilterValuesEqual } from "../../../utils/filterValueUtils";
import {
  buildSelectorOptions,
  getEffectiveSelectorOptions,
  getMultiChangeValue,
  getSelectedMultiItems,
  getSelectedSingleItem,
  getSingleChangeValue,
  isSelectorDisabledByDependency,
  normalizeSelectorValue,
} from "./utils";

const useSelectorState = ({ config, value, onChange, parentValue }) => {
  const prevValueRef = useRef(value);
  const onChangeRef = useRef(onChange);

  const {
    selectorType,
    options = [],
    depends_on,
    allow_null = false,
    null_label = "",
  } = config;

  const isMulti = selectorType === "multiple_selector";

  const effectiveOptions = useMemo(() => {
    return getEffectiveSelectorOptions({
      dependsOn: depends_on,
      parentValue,
      options,
    });
  }, [depends_on, parentValue, options]);

  const fullOptions = useMemo(() => {
    return buildSelectorOptions({
      isMulti,
      allowNull: allow_null,
      nullLabel: null_label,
      effectiveOptions,
    });
  }, [effectiveOptions, isMulti, allow_null, null_label]);

  const disabledByDependency = isSelectorDisabledByDependency({
    dependsOn: depends_on,
    parentValue,
  });

  const safeOnChange = useCallback((nextValue) => {
    const prev = prevValueRef.current;
    if (areFilterValuesEqual(prev, nextValue)) return;
    prevValueRef.current = nextValue;
    onChangeRef.current?.(nextValue);
  }, []);

  useEffect(() => {
    prevValueRef.current = value;
  }, [value]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const normalized = normalizeSelectorValue({
      isMulti,
      value,
      fullOptions,
      allowNull: allow_null,
      nullLabel: null_label,
      disabledByDependency,
    });

    if (normalized !== undefined) {
      safeOnChange(normalized);
    }
  }, [
    isMulti,
    value,
    fullOptions,
    safeOnChange,
    allow_null,
    null_label,
    disabledByDependency,
  ]);

  const selectedArray = Array.isArray(value) ? value : [];

  const selectedItems = useMemo(() => {
    if (!isMulti) return [];
    return getSelectedMultiItems({
      value,
      allowNull: allow_null,
      nullLabel: null_label,
      fullOptions,
    });
  }, [isMulti, value, allow_null, null_label, fullOptions]);

  const selectedSingleItem = useMemo(() => {
    return getSelectedSingleItem({
      isMulti,
      value,
      allowNull: allow_null,
      nullLabel: null_label,
      fullOptions,
    });
  }, [isMulti, value, allow_null, null_label, fullOptions]);

  const handleMultiChange = useCallback(
    ({ selectedItems: nextSelectedItems }) => {
      safeOnChange(
        getMultiChangeValue({
          selectedItems: nextSelectedItems,
          allowNull: allow_null,
          nullLabel: null_label,
        })
      );
    },
    [safeOnChange, allow_null, null_label]
  );

  const handleSingleChange = useCallback(
    ({ selectedItem }) => {
      safeOnChange(
        getSingleChangeValue({
          selectedItem,
          nullLabel: null_label,
        })
      );
    },
    [safeOnChange, null_label]
  );

  return {
    isMulti,
    fullOptions,
    selectedArray,
    selectedItems,
    selectedSingleItem,
    disabledByDependency,
    handleMultiChange,
    handleSingleChange,
  };
};

export default useSelectorState;
